import React, { useContext, useEffect, useState } from 'react'
import { Modal, Button, SelectField, TextField } from 'src/componentv2'
import { surevyNames } from 'src/model'
import SurveyApiContext, { SurveyTrigger } from 'src/provider/API/v2/call-service/Survey'

export interface NewSurveyTriggerModalProps {
    open: boolean
    onClose: () => void
    ibInstitutionCode: number | string
    onSaved: () => void
    isEdit?: boolean
    initialTrigger?: SurveyTrigger
}

interface TriggerForm {
    triggerName: string
    triggerDescription: string
    surveyInterval: string
}

const EMPTY: TriggerForm = { triggerName: '', triggerDescription: '', surveyInterval: '' }

export const NewSurveyTriggerModal: React.FC<NewSurveyTriggerModalProps> = ({
    open, onClose, ibInstitutionCode, onSaved, isEdit = false, initialTrigger,
}) => {
    const { saveSurveyTrigger } = useContext(SurveyApiContext)
    const [form, setForm] = useState<TriggerForm>(EMPTY)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setForm(initialTrigger ? {
            triggerName: initialTrigger.triggerName,
            triggerDescription: initialTrigger.triggerDescription,
            surveyInterval: String(initialTrigger.surveyInterval ?? ''),
        } : EMPTY)
    }, [open, initialTrigger])

    const isValid = form.triggerName.trim().length > 0
        && form.triggerDescription.trim().length > 0
        && Number(form.surveyInterval) > 0

    const handleSave = async () => {
        setSaving(true)
        try {
            const payload = {
                ibInstitutionCode,
                ...(isEdit && initialTrigger ? { id: initialTrigger.id } : {}),
                triggerName: form.triggerName,
                triggerDescription: form.triggerDescription,
                surveyInterval: Number(form.surveyInterval),
            }

            const result = await saveSurveyTrigger(payload)
            if (result) {
                onSaved()
                onClose()
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={isEdit ? 'Edit Survey Trigger' : 'Add New Survey Trigger'}
            maxWidth="48rem"
        >
            <SelectField
                label="Survey Name"
                placeholder="Select survey name"
                value={form.triggerName}
                onChange={(value) => setForm((f) => ({ ...f, triggerName: value }))}
                options={surevyNames.map((option) => ({ label: option.label, value: option.value }))}
                required
            />

            <div style={{ marginTop: '1.6rem' }}>
                <TextField
                    label="Trigger Description"
                    placeholder="Enter trigger description"
                    value={form.triggerDescription}
                    onChange={(value) => setForm((f) => ({ ...f, triggerDescription: value }))}
                    required
                />
            </div>

            <div style={{ marginTop: '1.6rem' }}>
                <TextField
                    label="Interval (days)"
                    placeholder="Enter interval in days"
                    type="number"
                    value={form.surveyInterval}
                    onChange={(value) => setForm((f) => ({ ...f, surveyInterval: value }))}
                    required
                />
            </div>

            <div style={{ marginTop: '2.4rem', display: 'flex', justifyContent: 'flex-end', gap: '1.2rem' }}>
                <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button disabled={!isValid || saving} onClick={handleSave}>
                    {saving ? 'Saving...' : isEdit ? 'Update Trigger' : 'Create Trigger'}
                </Button>
            </div>
        </Modal>
    )
}

export default NewSurveyTriggerModal
